import React, { useEffect, useState } from "react";
import TalentIgConnect, { type TalentIgEntry } from "./TalentIgConnect";
import { usersApi } from "../services/users";
import { startInstagramLogin } from "../lib/instagramLogin";
import { absMediaUrl } from "../utils/mediaUrl";
import type { User } from "../types/User";

// Connect Instagram over Edit profile (components/TalentIgConnect), for a
// signed-in talent. Kept out of pages/ProfilePage.tsx. Three ways in:
//   "connect"  Verify, or a tap on the Instagram box — both Instagram
//              switches on (founder, build 52): the screen first, then
//              Meta's sign-in from its Connect button.
//   "done"     back from Meta's sign-in, vouched: "Instagram connected!"
//              with the handle the server now holds.
//   "personal" back from Meta's sign-in, personal account: the message
//              (the message's switch).
// Anything else — a switch off, an older server without the flags, nothing
// vouched after all — renders nothing: the drawer, exactly as before.

type Props = {
  entry: TalentIgEntry;
  profile: User;
  /** The name in the drawer (maybe not saved yet), for the "you" bubble. */
  name: string;
  /** The profile as the server has it now: handle, proof, maybe the approval. */
  onProfile: (fresh: User) => void;
  /** That Instagram already vouches for another talent. */
  onTaken: () => void;
  onClose: () => void;
};

export default function ProfileIgConnect({ entry, profile, name, onProfile, onTaken, onClose }: Props) {
  const on =
    entry === "personal"
      ? !!profile.instagramDmVerifyEnabled
      : entry === "done"
        ? !!profile.instagramLoginEnabled
        : !!profile.instagramLoginEnabled && !!profile.instagramDmVerifyEnabled;
  // "done": the handle Instagram vouched for, read fresh (null while reading).
  const [vouched, setVouched] = useState<string | null>(entry === "done" ? null : "");

  // The server wrote the handle, Instagram's proof and maybe the approval
  // (auto-approval by followers): read them back, and let the screens under
  // the drawer hear it.
  const reread = (): Promise<User> =>
    usersApi.get(profile.slug).then((fresh) => {
      const data = fresh as User;
      onProfile(data);
      window.dispatchEvent(new CustomEvent("profile:patched", { detail: { instagram: data.instagram, approvalStatus: data.approvalStatus } }));
      return data;
    });

  useEffect(() => {
    if (entry !== "done" || !on) return;
    let alive = true;
    reread()
      .then((data) => alive && setVouched(data.instagramVerification?.username || ""))
      .catch(() => alive && setVouched(""));
    return () => {
      alive = false;
    };
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!on || (entry === "done" && !vouched)) return null;

  return (
    <TalentIgConnect
      name={name}
      avatarUrl={profile.avatarUrl ? absMediaUrl(profile.avatarUrl) : null}
      entry={entry}
      username={vouched || undefined}
      startLogin={() => startInstagramLogin("profile")}
      onVerified={(username) => {
        reread().catch(() =>
          onProfile({ ...profile, instagram: username, instagramVerification: { username, verifiedAt: new Date().toISOString() } })
        );
      }}
      onTaken={onTaken}
      onClose={onClose}
    />
  );
}
